
import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Client } from "@/types/client";
import { useClientStatusOptions } from "@/hooks/useClientStatusOptions";

type FormData = Omit<Client, "id" | "created_at" | "updated_at" | "client_assignments">;

interface ClientInfoInputsProps {
  formData: FormData;
  onFormChange: (data: Partial<FormData>) => void;
}

export const ClientInfoInputs = ({ formData, onFormChange }: ClientInfoInputsProps) => {
  const { statusOptions = {}, isLoading: statusLoading } = useClientStatusOptions();
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFormChange({ [e.target.id]: e.target.value });
  };

  const handleStatusChange = (value: Client["status"]) => {
    onFormChange({ status: value });
  };
  
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="name_ar" required>الاسم بالعربية</Label>
          <Input
            id="name_ar"
            value={formData.name_ar}
            onChange={handleChange}
            placeholder="الاسم الكامل بالعربية"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
          <Input
            id="name_en"
            value={formData.name_en || ''}
            onChange={handleChange}
            placeholder="Full Name in English"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="mobile">رقم الجوال</Label>
          <Input
            id="mobile"
            value={formData.mobile || ''}
            onChange={handleChange}
            placeholder="+966xxxxxxxxx"
            type="tel"
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="status" required>الحالة</Label>
          <Select
            value={formData.status}
            onValueChange={handleStatusChange}
            disabled={statusLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder={statusLoading ? "جاري التحميل..." : "اختر حالة العميل"}>
                {formData.status && statusOptions[formData.status] && (
                  <Badge 
                    variant="secondary"
                    style={{ 
                      backgroundColor: `${(statusOptions[formData.status] as any)?.color}20`,
                      color: (statusOptions[formData.status] as any)?.color,
                      borderColor: `${(statusOptions[formData.status] as any)?.color}40`
                    }}
                  >
                    {((statusOptions[formData.status] as any)?.label) || formData.status}
                  </Badge>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusOptions || {}).map(([key, statusInfo]) => {
                if (!statusInfo || typeof statusInfo !== 'object') return null;
                const typedStatus = statusInfo as { label: string; color: string };
                return (
                  <SelectItem key={key} value={key}>
                    <Badge 
                      variant="secondary"
                      style={{ 
                        backgroundColor: `${typedStatus.color}20`,
                        color: typedStatus.color,
                        borderColor: `${typedStatus.color}40`
                      }}
                    >
                      {typedStatus.label || key}
                    </Badge>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="additional_info">معلومات إضافية</Label>
        <Textarea
          id="additional_info"
          value={formData.additional_info || ''}
          onChange={handleChange}
          placeholder="أي معلومات إضافية عن العميل..."
          rows={3}
        />
      </div>
    </>
  );
};
